/**
 * Approval Repository Implementation
 * Implements the ApprovalRepository interface
 * Coordinates between API calls and mappers
 */

import {
  ApprovalRepository,
  GetApprovalsParams,
} from "../../domain/repositories/ApprovalRepository";
import {
  ApprovalEntity,
  ApprovalCountsEntity,
  ApprovalsResultEntity,
} from "../../domain/entities/ApprovalEntity";
import {
  getApprovalCounts,
  getApprovals,
  approveReview,
  rejectReview,
  getMonthEndChecklistReviews,
  approveMonthEndChecklistReview,
  rejectMonthEndChecklistReview,
} from "../api/approvalsApi";
import { ApprovalMapper } from "../mappers/ApprovalMapper";

export class ApprovalRepositoryImpl implements ApprovalRepository {
  /**
   * Get approval counts grouped by entity type and status
   */
  async getCounts(
    mode: "approvals" | "my-requests" = "approvals",
  ): Promise<ApprovalCountsEntity> {
    const response = await getApprovalCounts(mode);
    return ApprovalMapper.toApprovalCountsEntity(response);
  }

  /**
   * Get paginated approvals with filters
   */
  async getApprovals(
    params?: GetApprovalsParams,
  ): Promise<ApprovalsResultEntity> {
    // For month_end_checklist_item, use the month-end checklist reviews API
    const entityType = params?.entityType;
    if (entityType === "month_end_checklist_item") {
      const response = await getMonthEndChecklistReviews({
        status: params?.status,
        limit: params?.limit,
        offset: params?.offset,
      });
      return ApprovalMapper.toApprovalsResultEntity(response);
    }

    // For other entity types, use the regular approvals API
    const response = await getApprovals(params);
    return ApprovalMapper.toApprovalsResultEntity(response);
  }

  /**
   * Approve a review request
   */
  async approveReview(reviewId: number, comments?: string): Promise<void> {
    // Check if this is a month-end checklist review by checking the entity type
    // We need to determine this from the review data, but since we don't have it here,
    // we'll need to pass entityType as a parameter or check it differently
    // For now, we'll use a separate method that can be called with entityType
    await approveReview(reviewId, comments);
  }

  /**
   * Approve a review request (with entity type check)
   */
  async approveReviewWithEntityType(
    reviewId: number,
    entityType: string,
    comments?: string,
  ): Promise<void> {
    if (entityType === "month_end_checklist_item") {
      await approveMonthEndChecklistReview(reviewId, comments);
    } else {
      await approveReview(reviewId, comments);
    }
  }

  /**
   * Reject a review request
   */
  async rejectReview(reviewId: number, comments: string): Promise<void> {
    await rejectReview(reviewId, comments);
  }

  /**
   * Reject a review request (with entity type check)
   */
  async rejectReviewWithEntityType(
    reviewId: number,
    entityType: string,
    comments: string,
  ): Promise<void> {
    if (entityType === "month_end_checklist_item") {
      await rejectMonthEndChecklistReview(reviewId, comments);
    } else {
      await rejectReview(reviewId, comments);
    }
  }
}
